import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Plus, Search, ListTodo, CheckCircle2, X, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PageHeader } from "@/components/PageHeader";
import { EmptyState } from "@/components/EmptyState";
import { TaskItem } from "@/components/tasks/TaskItem";
import { TaskDialog } from "@/components/tasks/TaskDialog";
import { QuickAddTask } from "@/components/tasks/QuickAddTask";
import { fetchTasks, useTaskSummary, dueGroup, GROUP_LABEL, GROUP_ORDER, PRIORITY_OPTIONS, TASKS_CHANGED } from "@/lib/tasks";
import { cn } from "@/lib/utils";

const STATUS_TABS = [
  { value: "open", label: "Opne" },
  { value: "done", label: "Fullførte" },
  { value: "all", label: "Alle" },
];

export default function Huskeliste() {
  const [params, setParams] = useSearchParams();
  const status = params.get("status") || "open";
  const priority = params.get("prio") || "all";
  const machineId = params.get("machine") || "";
  const customerId = params.get("customer") || "";
  const [q, setQ] = useState(params.get("q") || "");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [dialog, setDialog] = useState({ open: false, task: null, defaults: null });
  const summary = useTaskSummary();

  const setParam = useCallback((patch) => {
    setParams((prev) => {
      const p = new URLSearchParams(prev);
      Object.entries(patch).forEach(([k, v]) => { if (!v || v === "all" || v === "open") p.delete(k); else p.set(k, v); });
      return p;
    }, { replace: true });
  }, [setParams]);

  const load = useCallback((silent = false) => {
    if (!silent) setLoading(true);
    setError(false);
    fetchTasks({ status, priority: priority !== "all" ? priority : undefined, machine_id: machineId || undefined, customer_id: customerId || undefined, limit: 500 })
      .then(setData)
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [status, priority, machineId, customerId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const fn = () => load(true);
    window.addEventListener(TASKS_CHANGED, fn);
    return () => window.removeEventListener(TASKS_CHANGED, fn);
  }, [load]);
  useEffect(() => {
    if (params.get("ny") === "1") {
      setDialog({ open: true, task: null, defaults: { machine_id: machineId, customer_id: customerId } });
      setParam({ ny: null });
    }
  }, [params, machineId, customerId, setParam]);

  const items = useMemo(() => {
    const list = data?.items || [];
    const needle = q.trim().toLowerCase();
    if (!needle) return list;
    return list.filter((t) => [t.title, t.description, t.machine_label, t.customer_name, t.created_by_name].filter(Boolean).some((s) => s.toLowerCase().includes(needle)));
  }, [data, q]);

  const groups = useMemo(() => {
    const g = {};
    items.forEach((t) => { const k = dueGroup(t); (g[k] = g[k] || []).push(t); });
    if (g.done) g.done.sort((a, b) => (b.completed_at || "").localeCompare(a.completed_at || ""));
    return GROUP_ORDER.filter((k) => g[k]?.length).map((k) => ({ key: k, label: GROUP_LABEL[k], items: g[k] }));
  }, [items]);

  const onChange = (updated, original) => {
    setData((prev) => {
      if (!prev) return prev;
      let list = prev.items;
      if (!updated) list = list.filter((t) => t.id !== original.id);
      else if (status !== "all" && updated.status !== status) list = list.filter((t) => t.id !== updated.id);
      else list = list.map((t) => (t.id === updated.id ? updated : t));
      return { ...prev, items: list, total: list.length };
    });
  };

  const linkFilterActive = !!(machineId || customerId);
  const linkedLabel = useMemo(() => {
    const t = (data?.items || []).find((x) => (machineId && x.machine_id === machineId) || (customerId && x.customer_id === customerId));
    return machineId ? t?.machine_label || "vald maskin" : t?.customer_name || "vald kunde";
  }, [data, machineId, customerId]);

  return (
    <div className="space-y-5" data-testid="tasks-page">
      <PageHeader
        title="Huskeliste"
        description="Små og store jobbar som skal gjerast, men som ikkje er del av servicefristane. Alle på laget kan leggje inn og sjå."
        actions={<Button onClick={() => setDialog({ open: true, task: null, defaults: { machine_id: machineId, customer_id: customerId } })} data-testid="tasks-new-button"><Plus /> Nytt punkt</Button>}
      />

      <Card className="shadow-sm">
        <CardContent className="p-3 sm:p-4">
          <QuickAddTask
            defaults={{ machine_id: machineId || "", customer_id: customerId || "" }}
            onMore={(title) => setDialog({ open: true, task: null, defaults: { title, machine_id: machineId, customer_id: customerId } })}
            testid="tasks-quick-add"
          />
        </CardContent>
      </Card>

      {/* Filter */}
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div className="flex gap-1 overflow-x-auto rounded-lg bg-muted p-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden" role="tablist" data-testid="tasks-status-tabs">
          {STATUS_TABS.map((t) => {
            const count = t.value === "open" ? summary?.open : t.value === "done" ? summary?.done : null;
            const active = status === t.value;
            return (
              <button key={t.value} type="button" role="tab" aria-selected={active} onClick={() => setParam({ status: t.value })} data-testid={`tasks-tab-${t.value}`}
                className={cn("pressable inline-flex h-10 shrink-0 items-center gap-1.5 rounded-md px-3.5 text-sm font-semibold transition-colors", active ? "bg-card text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground")}>
                {t.label}{count != null && <span className={cn("rounded-full px-1.5 text-[11px] tabular-nums", active ? "bg-muted" : "bg-card/60")}>{count}</span>}
              </button>
            );
          })}
        </div>
        <div className="flex flex-1 gap-2 md:max-w-lg md:justify-end">
          <div className="relative min-w-0 flex-1">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Søk i lista …" className="h-10 pl-9" data-testid="tasks-search" />
          </div>
          <Select value={priority} onValueChange={(v) => setParam({ prio: v })}>
            <SelectTrigger className="h-10 w-[128px] shrink-0" data-testid="tasks-priority-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alle prioritetar</SelectItem>
              {PRIORITY_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value} data-testid={`tasks-priority-filter-${o.value}`}>{o.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      {linkFilterActive && (
        <div className="flex items-center gap-2 text-sm" data-testid="tasks-link-filter">
          <span className="text-muted-foreground">Viser punkt for</span>
          <span className="inline-flex h-8 items-center gap-1.5 rounded-full border bg-card px-3 font-semibold">
            {linkedLabel}
            <button type="button" onClick={() => setParam({ machine: null, customer: null })} aria-label="Fjern filter" className="grid h-6 w-6 place-items-center rounded-full hover:bg-accent" data-testid="tasks-clear-link-filter"><X className="h-3.5 w-3.5" /></button>
          </span>
        </div>
      )}

      {/* Liste */}
      {loading ? (
        <div className="space-y-2">{[...Array(5)].map((_, i) => <Skeleton key={i} className="h-16 w-full" />)}</div>
      ) : error ? (
        <EmptyState icon={RefreshCw} title="Kunne ikkje hente huskelista" description="Sjekk nettet og prøv igjen." action={<Button onClick={() => load()}><RefreshCw /> Prøv igjen</Button>} />
      ) : items.length === 0 ? (
        status === "done" ? (
          <EmptyState icon={CheckCircle2} title="Ingen fullførte punkt enno" description="Når nokon markerer eit punkt som fullført, blir det liggjande her som historikk." />
        ) : q.trim() ? (
          <EmptyState icon={Search} title="Ingen treff" description={`Fann ingen punkt som matchar «${q.trim()}».`} action={<Button variant="outline" onClick={() => setQ("")}>Tøm søk</Button>} />
        ) : (
          <EmptyState icon={ListTodo} title="Huskelista er tom" description="Skriv inn det første punktet over – ei slange som bør bytast, ein del som skal bestillast, noko som skal ryddast." action={<Button onClick={() => setDialog({ open: true, task: null, defaults: null })}><Plus /> Nytt punkt</Button>} />
        )
      ) : (
        <div className="space-y-5" data-testid="tasks-list">
          {groups.map((g) => (
            <section key={g.key} data-testid={`tasks-group-${g.key}`}>
              <h2 className={cn("mb-1.5 flex items-center gap-2 px-1 text-xs font-semibold uppercase tracking-wider", g.key === "overdue" ? "text-[rgb(var(--status-overdue))]" : "text-muted-foreground")}>
                {g.label} <span className="rounded-full bg-muted px-1.5 text-[11px] tabular-nums text-muted-foreground">{g.items.length}</span>
              </h2>
              <Card className="shadow-sm">
                <CardContent className="divide-y divide-border p-1.5 sm:p-2">
                  {g.items.map((t) => (
                    <TaskItem key={t.id} task={t} onEdit={(task) => setDialog({ open: true, task, defaults: null })} onChange={(u) => onChange(u, t)} />
                  ))}
                </CardContent>
              </Card>
            </section>
          ))}
        </div>
      )}

      <TaskDialog open={dialog.open} onOpenChange={(o) => setDialog((d) => ({ ...d, open: o }))} task={dialog.task} defaults={dialog.defaults} onSaved={() => load(true)} />
    </div>
  );
}
